import { z } from 'zod'

import {
  missionKindSchema,
  operationEventSchema,
  operationStateKindSchema,
  operationStateSchema,
  operationStatusSchema,
  operationTaskSchema,
  operationTaskStatusSchema,
} from './operations.js'

// Module Opérations (0.6.0, migration 20261115010000) : liste, fiche, bilan de clôture, clôture avec exceptions et
// annulation d'une opération. Règles : docs/operations/README.md. Additif : aucun schéma 0.5.0 n'est modifié.

const timestampSchema = z.string().refine((value) => !Number.isNaN(Date.parse(value)), 'Horodatage invalide')
const nullableTimestampSchema = timestampSchema.nullable()
const uuidSchema = z.uuid()
const countSchema = z.number().int().nonnegative()

// ------------------------------------------------------------------ Énumérations

/** Résultat de la clôture, distinct du statut technique CLOSED. */
export const operationCloseOutcomeSchema = z.enum(['COMPLETED', 'WITH_EXCEPTIONS'])
export type OperationCloseOutcome = z.infer<typeof operationCloseOutcomeSchema>

/** Vues rapides de la liste (filtre serveur). */
export const operationsViewSchema = z.enum([
  'ALL',
  'ACTIVE',
  'TO_CLOSE',
  'WITH_EXCEPTIONS',
  'CLOSED',
  'CANCELLED',
])
export type OperationsView = z.infer<typeof operationsViewSchema>

export const operationResidenceFilterSchema = z.enum([
  'ALL',
  'REMAINING',
  'PROBLEM',
  'DONE',
  'RESOLVED_NO_SERVICE',
  'NOT_DONE_AT_CLOSE',
])
export type OperationResidenceFilter = z.infer<typeof operationResidenceFilterSchema>

export const closeDiagnosticKeySchema = z.enum([
  'OPEN_MISSIONS',
  'UNRESOLVED_TASKS',
  'ACTIVE_OFFERS',
  'OPEN_CONFLICTS',
  'HELP_REQUESTS',
  'PREPARED_PASSES',
])
export type CloseDiagnosticKey = z.infer<typeof closeDiagnosticKeySchema>

export const closeDiagnosticSeveritySchema = z.enum(['BLOCKER', 'WARNING', 'OK'])
export type CloseDiagnosticSeverity = z.infer<typeof closeDiagnosticSeveritySchema>

// ------------------------------------------------------------------ Lectures

/**
 * Compteurs d'interventions (résidence × passage). Résidences uniques ≠ interventions; problèmes ⊂ restantes;
 * non effectuées à la clôture séparées des restantes et des résolues sans déneigement.
 */
export const operationModuleCountersSchema = z.object({
  activeMissions: countSchema,
  totalMissions: countSchema,
  passes: countSchema,
  uniqueResidences: countSchema,
  expectedInterventions: countSchema,
  done: countSchema,
  resolvedNoService: countSchema,
  remaining: countSchema,
  problems: countSchema,
  notDoneAtClose: countSchema,
  notDoneProblems: countSchema,
  retries: countSchema,
  activeOffers: countSchema,
  openConflicts: countSchema,
})
export type OperationModuleCounters = z.infer<typeof operationModuleCountersSchema>

export const operationModuleRowSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  season: z.string(),
  status: operationStatusSchema,
  version: z.number().int().positive(),
  state: operationStateKindSchema,
  openedAt: timestampSchema,
  /** Première mission valide (démarrage accidentel exclu). */
  firstMissionAt: nullableTimestampSchema,
  /** Dernière fermeture valide, quand plus aucune mission n'est ouverte. */
  serviceEndedAt: nullableTimestampSchema,
  /** Clôture administrative par le bureau. */
  closedAt: nullableTimestampSchema,
  cancelledAt: nullableTimestampSchema,
  closeOutcome: operationCloseOutcomeSchema.nullable(),
  stale: z.boolean(),
  counters: operationModuleCountersSchema,
})
export type OperationModuleRow = z.infer<typeof operationModuleRowSchema>

export const operationsListV2Schema = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  timezone: z.string(),
  total: countSchema,
  views: z.record(operationsViewSchema, countSchema),
  seasons: z.array(z.string()),
  rows: z.array(operationModuleRowSchema),
})
export type OperationsListV2 = z.infer<typeof operationsListV2Schema>

export const operationModuleMissionSchema = z.object({
  id: uuidSchema,
  kind: missionKindSchema,
  status: z.string().nullable(),
  cancelKind: z.string().nullable(),
  routeId: uuidSchema.nullable(),
  routeName: z.string().nullable(),
  operatorName: z.string().nullable(),
  equipmentLabel: z.string().nullable(),
  startedAt: nullableTimestampSchema,
  endedAt: nullableTimestampSchema,
  doneTasks: countSchema,
  /** Passage engagé par une mission normale (nul pour un renfort). */
  passNumber: z.number().int().positive().nullable().optional(),
  /** Routes d'origine des tâches portées par la mission (un renfort peut en avoir plusieurs). */
  routes: z.array(uuidSchema),
})
export type OperationModuleMission = z.infer<typeof operationModuleMissionSchema>

export const routePassStatusSchema = z.enum(['PREPARED', 'STARTED', 'CANCELLED'])
export type RoutePassStatus = z.infer<typeof routePassStatusSchema>

export const operationRoutePassSchema = z.object({
  passNumber: z.number().int().positive(),
  /** 0.6.0 (migration 20261115020000) : entité passage; absente avant. */
  passId: uuidSchema.nullable().optional(),
  status: routePassStatusSchema.optional(),
  preparedAt: nullableTimestampSchema.optional(),
  startedAt: nullableTimestampSchema.optional(),
  total: countSchema,
  done: countSchema,
  remaining: countSchema,
  problems: countSchema,
  resolvedNoService: countSchema,
  notDoneAtClose: countSchema,
})
export type OperationRoutePass = z.infer<typeof operationRoutePassSchema>

export const operationDetailSchema = operationModuleRowSchema.extend({
  serverTime: timestampSchema,
  timezone: z.string(),
  stateDetail: operationStateSchema,
  closedByName: z.string().nullable(),
  closeNote: z.string().nullable(),
  cancelledByName: z.string().nullable(),
  cancelledReason: z.string().nullable(),
  reopenedAt: nullableTimestampSchema,
  reopenedByName: z.string().nullable(),
  reopenedReason: z.string().nullable(),
  missions: z.array(operationModuleMissionSchema),
  routes: z.array(
    z.object({
      routeId: uuidSchema.nullable(),
      name: z.string().nullable(),
      color: z.string().nullable(),
      passes: z.array(operationRoutePassSchema),
    }),
  ),
})
export type OperationDetail = z.infer<typeof operationDetailSchema>

export const operationResidenceSchema = operationTaskSchema.extend({
  closureDisposition: z.literal('NOT_DONE_AT_CLOSE').nullable(),
  closureReason: z.string().nullable(),
  closureAt: nullableTimestampSchema,
  closureByName: z.string().nullable(),
  doneByName: z.string().nullable(),
})
export type OperationResidence = z.infer<typeof operationResidenceSchema>

export const operationResidencesSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: countSchema,
  rows: z.array(operationResidenceSchema),
})
export type OperationResidences = z.infer<typeof operationResidencesSchema>

export const operationJournalSchema = z.object({
  events: z.array(operationEventSchema),
  nextBeforeId: z.number().int().positive().nullable(),
})
export type OperationJournal = z.infer<typeof operationJournalSchema>

// ------------------------------------------------------------------ Bilan de clôture

const diagnosticCategoryBase = { count: countSchema, severity: closeDiagnosticSeveritySchema }

export const closeDiagnosticCategorySchema = z.discriminatedUnion('key', [
  z.object({
    key: z.literal('OPEN_MISSIONS'),
    ...diagnosticCategoryBase,
    items: z.array(
      z.object({
        missionId: uuidSchema,
        kind: missionKindSchema,
        status: z.string(),
        operatorName: z.string().nullable(),
        equipmentLabel: z.string().nullable(),
        routeName: z.string().nullable(),
        startedAt: nullableTimestampSchema,
        pausedAt: nullableTimestampSchema,
      }),
    ),
  }),
  z.object({
    key: z.literal('UNRESOLVED_TASKS'),
    ...diagnosticCategoryBase,
    problems: countSchema,
    truncated: z.boolean(),
    items: z.array(
      z.object({
        taskId: uuidSchema,
        status: operationTaskStatusSchema,
        passNumber: z.number().int().positive(),
        routeId: uuidSchema.nullable(),
        routeName: z.string().nullable(),
        routeColor: z.string().nullable(),
        address: z.string().nullable(),
        needsReassignment: z.boolean(),
        ownerName: z.string().nullable(),
      }),
    ),
  }),
  z.object({
    key: z.literal('ACTIVE_OFFERS'),
    ...diagnosticCategoryBase,
    items: z.array(
      z.object({
        offerId: uuidSchema,
        taskId: uuidSchema,
        createdAt: timestampSchema,
        address: z.string().nullable(),
        routeName: z.string().nullable(),
      }),
    ),
  }),
  z.object({
    key: z.literal('OPEN_CONFLICTS'),
    ...diagnosticCategoryBase,
    items: z.array(
      z.object({
        conflictId: uuidSchema,
        kind: z.string(),
        taskId: uuidSchema,
        missionId: uuidSchema.nullable(),
        createdAt: timestampSchema,
        operatorName: z.string().nullable(),
        address: z.string().nullable(),
      }),
    ),
  }),
  z.object({
    key: z.literal('PREPARED_PASSES'),
    ...diagnosticCategoryBase,
    items: z.array(
      z.object({
        passId: uuidSchema,
        routeId: uuidSchema,
        routeName: z.string().nullable(),
        routeColor: z.string().nullable(),
        passNumber: z.number().int().positive(),
        preparedAt: nullableTimestampSchema,
      }),
    ),
  }),
  z.object({
    key: z.literal('HELP_REQUESTS'),
    ...diagnosticCategoryBase,
    items: z.array(
      z.object({
        helpRequestId: uuidSchema,
        reason: z.string(),
        status: z.string(),
        createdAt: timestampSchema,
        missionId: uuidSchema.nullable(),
        operatorName: z.string().nullable(),
      }),
    ),
  }),
])
export type CloseDiagnosticCategory = z.infer<typeof closeDiagnosticCategorySchema>

const outcomeBase = { available: z.boolean(), reason: z.string().nullable() }

export const closeDiagnosticSchema = z.object({
  operationId: uuidSchema,
  number: z.string(),
  status: operationStatusSchema,
  version: z.number().int().positive(),
  /** Empreinte des données décisives : la commande la compare, sous verrou, à celle du moment. */
  fingerprint: z.string().min(1),
  readAt: timestampSchema,
  state: operationStateSchema,
  hasWork: z.boolean(),
  categories: z.array(closeDiagnosticCategorySchema),
  outcomes: z.object({
    close: z.object(outcomeBase),
    closeWithExceptions: z.object({
      ...outcomeBase,
      notDone: countSchema,
      problems: countSchema,
      offersToRevoke: countSchema,
      passesToCancel: countSchema.optional(),
    }),
    cancel: z.object({ ...outcomeBase, offersToRevoke: countSchema, passesToCancel: countSchema.optional() }),
  }),
})
export type CloseDiagnostic = z.infer<typeof closeDiagnosticSchema>

// ------------------------------------------------------------------ Commandes

const replayedSchema = z.object({ replayed: z.boolean() })

export const closeWithExceptionsResultSchema = replayedSchema.extend({
  operationId: uuidSchema,
  status: operationStatusSchema,
  version: z.number().int().positive(),
  closedAt: timestampSchema,
  closeOutcome: operationCloseOutcomeSchema,
  notDone: countSchema,
  problems: countSchema,
  offersRevoked: countSchema,
  passesCancelled: countSchema.optional(),
})
export type CloseWithExceptionsResult = z.infer<typeof closeWithExceptionsResultSchema>

export const cancelOperationResultSchema = replayedSchema.extend({
  operationId: uuidSchema,
  status: operationStatusSchema,
  version: z.number().int().positive(),
  cancelledAt: timestampSchema,
  withdrawnTasks: countSchema,
  offersRevoked: countSchema,
  passesCancelled: countSchema.optional(),
})
export type CancelOperationResult = z.infer<typeof cancelOperationResultSchema>

// ------------------------------------------------------------------ Passages de route (migration 20261115020000)

/** Action possible sur une route de l'opération ouverte, décidée par le serveur. */
export const routeActionSchema = z.enum(['START', 'RESUME', 'NEW_PASS', 'IN_USE', 'COMPLETED'])
export type RouteAction = z.infer<typeof routeActionSchema>

const routeStateShape = {
  action: routeActionSchema,
  /** Passage que le prochain départ engagerait. */
  passNumber: z.number().int().positive(),
  currentPass: z.number().int().positive().nullable(),
  preparedPassId: uuidSchema.nullable(),
  total: countSchema,
  done: countSchema,
  remaining: countSchema,
  inUseBy: z.string().nullable(),
}

export const routePassesOverviewSchema = z.object({
  operationId: uuidSchema,
  version: z.number().int().positive(),
  readAt: timestampSchema,
  routes: z.array(
    z.object({
      routeId: uuidSchema,
      name: z.string().nullable(),
      color: z.string().nullable(),
      eligibleNow: countSchema,
      ...routeStateShape,
      canPrepare: z.boolean(),
      reason: z.string().nullable(),
    }),
  ),
})
export type RoutePassesOverview = z.infer<typeof routePassesOverviewSchema>

export const prepareRoutePassesResultSchema = replayedSchema.extend({
  operationId: uuidSchema,
  passes: z.array(
    z.object({
      passId: uuidSchema,
      routeId: uuidSchema,
      passNumber: z.number().int().positive(),
      eligibleResidences: countSchema,
    }),
  ),
})
export type PrepareRoutePassesResult = z.infer<typeof prepareRoutePassesResultSchema>

export const cancelRoutePassResultSchema = replayedSchema.extend({
  passId: uuidSchema,
  status: routePassStatusSchema,
})

/** Catégorie de route (`routes.category`) : jamais déduite du nom (« 1 » / « A » reste un libellé). */
export const routeCategorySchema = z.enum(['RESIDENTIAL', 'LARGE_SITE'])
export type RouteCategory = z.infer<typeof routeCategorySchema>

/**
 * Tablette (`operator_route_plan_v1`) : routes de l'opérateur dans l'ordre du bureau, action possible dans
 * l'opération ouverte, prochaine route admissible. Rien ne démarre sans intention de l'opérateur.
 *
 * 0.8.0 (Routes › Répartition, migration `20261209030000`) : clés facultatives `planVersion`, `routeCount`, et par
 * route `routeIndex`, `routeCategory`, `nextRoute` (« Route 2 sur 3 — Saint-Jérôme A — Suivante : Saint-Antoine 3 »).
 * Absentes (serveur plus ancien), la tablette garde l'affichage 0.7.0.
 */
export const operatorRoutePlanSchema = z.object({
  operation: z
    .object({
      id: uuidSchema,
      number: z.string(),
      status: operationStatusSchema,
      openedAt: timestampSchema,
      ageHours: z.number(),
      stale: z.boolean(),
    })
    .nullable(),
  routes: z.array(
    z.object({
      rank: z.number().int().positive(),
      routeId: uuidSchema,
      name: z.string().nullable(),
      color: z.string().nullable(),
      description: z.string().nullable(),
      available: z.boolean(),
      eligibleNow: countSchema,
      ...routeStateShape,
      routeIndex: z.number().int().positive().optional(),
      routeCategory: routeCategorySchema.optional(),
      nextRoute: z
        .object({ routeId: uuidSchema, name: z.string().nullable(), routeCategory: routeCategorySchema })
        .nullable()
        .optional(),
    }),
  ),
  nextRouteId: uuidSchema.nullable(),
  /** Version publiée de la répartition dont vient la liste; `null` si la liste n'a pas été écrite par un plan. */
  planVersion: z.number().int().positive().nullable().optional(),
  routeCount: countSchema.optional(),
})
export type OperatorRoutePlan = z.infer<typeof operatorRoutePlanSchema>

/** Commande de départ 0.6.0 : clés facultatives ajoutées à `start_operator_mission_v1`. */
export interface StartMissionRouteChoice {
  /** Une des routes assignées; absente : route principale (rang 1). */
  routeId?: string
  /** Passage affiché à l'opérateur; différent de celui du serveur : refus MISSION_PASS_CHANGED. */
  expectedPassNumber?: number
}
